import { useRef, useState } from 'react';
import { Link } from 'react-router-dom';
import { ApiError } from '../lib/api';
import { useCodeLabel, useI18n } from '../lib/i18n';
import { useApiError } from '../lib/useApiError';
import { formatDateTime, formatGel } from '../lib/format';
import { useConfirmImport, useImport, useImports, useUploadImport } from '../lib/queries';
import { can, useSession } from '../lib/session';
import { Alert, Button, Card, EmptyState, Pill, SectionTitle, Skeleton } from '../components/ui';

const TEMPLATE = 'first_name,last_name,phone,email,historical_spend,vehicle_make,vehicle_model,vehicle_plate,vehicle_year\nGiorgi,Beridze,555 10 20 30,giorgi@example.ge,18500,BMW,M5,AA-111-AA,2022\n';

const ROW_TONE: Record<string, 'neutral' | 'warning' | 'danger'> = {
  DUPLICATE: 'warning', INVALID: 'danger', FAILED: 'danger', SKIPPED: 'warning',
};

export function ImportPage() {
  const { t } = useI18n();
  const code = useCodeLabel();
  const { data: session } = useSession();
  const { data: list } = useImports();
  const upload = useUploadImport();
  const confirm = useConfirmImport();
  const fileRef = useRef<HTMLInputElement>(null);
  const [selected, setSelected] = useState<string | null>(null);
  const { data: detail, isPending: detailPending } = useImport(selected);

  const mayConfirm = can(session, 'imports.confirm');
  const errorText = useApiError();
  const error = upload.error instanceof ApiError ? upload.error : null;

  const onFile = async (file: File) => {
    const content = await file.text();
    upload.mutate({ filename: file.name, content }, {
      onSuccess: (batch) => setSelected(batch.id),
    });
  };

  const downloadTemplate = () => {
    const blob = new Blob([TEMPLATE], { type: 'text/csv' });
    const url = URL.createObjectURL(blob);
    const anchor = document.createElement('a');
    anchor.href = url;
    anchor.download = 'auditoria-import-template.csv';
    anchor.click();
    URL.revokeObjectURL(url);
  };

  const batch = detail?.batch;
  const running = batch?.status === 'CONFIRMED' || batch?.status === 'PROCESSING';

  return (
    <div className="mx-auto max-w-4xl">
      <header className="mb-5">
        <h1 className="font-display text-[1.75rem] font-bold leading-tight tracking-tight text-ink sm:text-[2rem]">{t('import.title')}</h1>
      </header>

      {error && <div className="mb-4"><Alert>{errorText(error) ?? error.message}</Alert></div>}

      <Card className="mb-4">
        <SectionTitle action={
          <Button variant="quiet" size="sm" onClick={downloadTemplate}>{t('import.template')}</Button>
        }>{t('import.upload')}</SectionTitle>

        <input
          ref={fileRef}
          type="file"
          accept=".csv,text/csv"
          className="hidden"
          onChange={(event) => {
            const file = event.target.files?.[0];
            if (file) void onFile(file);
            event.target.value = '';
          }}
        />

        <button
          onClick={() => fileRef.current?.click()}
          className="flex w-full flex-col items-center gap-2 rounded-2xl border border-dashed border-line bg-raised/40 px-4 py-8 transition-colors hover:border-brand/50 hover:bg-raised"
        >
          <span className="text-sm font-medium text-ink">
            {upload.isPending ? t('import.reading') : t('import.chooseFile')}
          </span>
          <span className="text-xs text-ink-faint">
            {t('import.columnsHint')}
          </span>
        </button>

        <p className="mt-3 text-[0.7rem] leading-relaxed text-ink-faint">
          {t('import.confirmNote')}
        </p>
      </Card>

      {selected && detailPending && <Skeleton className="h-64" />}

      {batch && (
        <Card className="mb-4">
          <SectionTitle>{batch.filename}</SectionTitle>

          <div className="grid grid-cols-2 gap-3 sm:grid-cols-4">
            {[
              { label: 'import.rows' as const, value: batch.totalRows, tone: 'text-ink' },
              { label: 'import.valid' as const, value: batch.validRows, tone: 'text-positive' },
              { label: 'import.duplicates' as const, value: batch.duplicateRows, tone: 'text-warning' },
              { label: 'import.invalid' as const, value: batch.invalidRows, tone: 'text-danger' },
            ].map((cell) => (
              <div key={cell.label} className="rounded-xl border border-line bg-raised/40 px-3 py-2.5">
                <div className={`tabular text-lg font-bold ${cell.tone}`}>{cell.value}</div>
                <div className="text-[0.62rem] uppercase tracking-wider text-ink-faint">{t(cell.label)}</div>
              </div>
            ))}
          </div>

          {running && detail && (
            <div className="mt-4">
              <div className="flex justify-between text-xs text-ink-dim">
                <span>{t('import.writing')}</span>
                <span className="tabular">{detail.progress.percent}%</span>
              </div>
              <div className="mt-2 h-1.5 overflow-hidden rounded-full bg-muted">
                <div
                  className="h-full rounded-full bg-brand transition-[width] duration-500"
                  style={{ width: `${detail.progress.percent}%` }}
                />
              </div>
            </div>
          )}

          {batch.status === 'COMPLETED' && (
            <div className="mt-4">
              <Alert tone="success">
                {t('import.importedCount', { count: batch.importedRows })}
                {batch.failedRows > 0 && t('import.failedCount', { count: batch.failedRows })}.
              </Alert>
            </div>
          )}

          {batch.status === 'PREVIEWED' && mayConfirm && (
            <Button
              className="mt-4 w-full"
              loading={confirm.isPending}
              disabled={batch.validRows === 0}
              onClick={() => confirm.mutate(batch.id)}
            >
              {t('import.importButton', { count: batch.validRows })}
            </Button>
          )}

          {detail && detail.rows.length > 0 && (
            <div className="mt-4 max-h-96 space-y-1.5 overflow-y-auto border-t border-line pt-3">
              {detail.rows.map((row) => {
                const data = row.rawData as Record<string, string | number | null>;
                const spend = Number(data['historicalSpendTetri'] ?? 0);
                return (
                  <div
                    key={row.id}
                    className="flex items-center justify-between gap-3 rounded-lg px-2 py-1.5 text-xs"
                  >
                    <div className="flex min-w-0 items-center gap-2">
                      <span className="tabular w-6 shrink-0 text-ink-faint">{row.rowNumber}</span>
                      <span className="truncate text-ink">
                        {String(data['firstName'] ?? '')} {String(data['lastName'] ?? '')}
                      </span>
                      <span className="tabular shrink-0 text-ink-faint">{String(data['phone'] ?? '')}</span>
                    </div>
                    <div className="flex shrink-0 items-center gap-2">
                      {spend > 0 && <span className="tabular text-ink-dim">{formatGel(spend)}</span>}
                      {row.createdCustomerId ? (
                        <Link to={`/customers/${row.createdCustomerId}`} className="text-brand hover:underline">
                          {t('common.open')}
                        </Link>
                      ) : row.matchedCustomerId ? (
                        <Link to={`/customers/${row.matchedCustomerId}`} className="text-warning hover:underline">
                          {t('import.existing')}
                        </Link>
                      ) : null}
                      {ROW_TONE[row.status]
                        ? <Pill tone={ROW_TONE[row.status]}>{code('status', row.status)}</Pill>
                        : <Pill>{code('status', row.status)}</Pill>}
                    </div>
                  </div>
                );
              })}
            </div>
          )}
        </Card>
      )}

      <SectionTitle>{t('import.past')}</SectionTitle>
      {!list?.items.length
        ? <EmptyState title={t('import.empty')} />
        : (
          <div className="space-y-2">
            {list.items.map((item) => (
              <button
                key={item.id}
                onClick={() => setSelected(item.id)}
                className={`flex w-full items-center justify-between gap-3 rounded-2xl border bg-surface px-4 py-3 text-left transition-colors ${
                  selected === item.id ? 'border-brand/50' : 'border-line hover:border-ink-faint'
                }`}
              >
                <div className="min-w-0">
                  <div className="truncate text-sm text-ink">{item.filename}</div>
                  <div className="mt-0.5 text-xs text-ink-faint">{formatDateTime(item.createdAt)}</div>
                </div>
                <div className="flex shrink-0 items-center gap-2">
                  <span className="tabular text-xs text-ink-dim">
                    {item.importedRows}/{item.totalRows}
                  </span>
                  <Pill tone={item.status === 'COMPLETED' ? 'neutral' : 'warning'}>
                    {code('status', item.status)}
                  </Pill>
                </div>
              </button>
            ))}
          </div>
        )}
    </div>
  );
}
